import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  Button,
  Table,
  StatusIndicator,
  Spinner,
  BreadcrumbGroup,
  Alert,
  ColumnLayout,
  Tabs,
  Modal,
  TextContent,
  FormField,
  Input,
  Textarea,
  Select,
  Icon,
  TextFilter,
} from '@cloudscape-design/components';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { useNotifications } from './NotificationContext';
import AppLayoutWrapper from './AppLayoutWrapper';

export default function Telephony() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { error: notifyError, success: notifySuccess } = useNotifications();
  const [numbers, setNumbers] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('numbers');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [callsFilteringText, setCallsFilteringText] = useState('');
  const [callsSortingColumn, setCallsSortingColumn] = useState(null);
  const [callsSortingDescending, setCallsSortingDescending] = useState(true);
  const [numbersSortingColumn, setNumbersSortingColumn] = useState(null);
  const [numbersSortingDescending, setNumbersSortingDescending] = useState(true);

  const sortedNumbers = useMemo(() => {
    if (!numbersSortingColumn) return numbers;
    const sorted = [...numbers].sort((a, b) => {
      const f = numbersSortingColumn.sortingField;
      const getVal = (item) => {
        if (f === 'phone') return item.phone_number || '';
        if (f === 'status') return item.status || '';
        return '';
      };
      const aVal = getVal(a);
      const bVal = getVal(b);
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    });
    return numbersSortingDescending ? [...sorted].reverse() : sorted;
  }, [numbers, numbersSortingColumn, numbersSortingDescending]);

  const filteredCalls = useMemo(() => {
    if (!callsFilteringText) return calls;
    const lower = callsFilteringText.toLowerCase();
    return calls.filter((c) =>
      (c.id && c.id.toLowerCase().includes(lower)) ||
      (c.assistant?.company && c.assistant.company.toLowerCase().includes(lower))
    );
  }, [calls, callsFilteringText]);

  const sortedCalls = useMemo(() => {
    if (!callsSortingColumn) return filteredCalls;
    const sorted = [...filteredCalls].sort((a, b) => {
      const f = callsSortingColumn.sortingField;
      const getVal = (item) => {
        if (f === 'id') return item.id || '';
        if (f === 'company') return item.assistant?.company || '';
        if (f === 'created') return item.created_at || '';
        return '';
      };
      const aVal = getVal(a);
      const bVal = getVal(b);
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    });
    return callsSortingDescending ? [...sorted].reverse() : sorted;
  }, [filteredCalls, callsSortingColumn, callsSortingDescending]);

  const [sipList, setSipList] = useState([]);
  const [sipLoading, setSipLoading] = useState(false);
  const [sipModalVisible, setSipModalVisible] = useState(false);
  const [sipEditModalVisible, setSipEditModalVisible] = useState(false);
  const [sipDeleteModalVisible, setSipDeleteModalVisible] = useState(false);
  const [selectedSip, setSelectedSip] = useState(null);
  const [sipForm, setSipForm] = useState({ provider: '', name: '', credentials: '{}' });
  const [sipSubmitLoading, setSipSubmitLoading] = useState(false);

  // Agent SIP config state
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [agentSipConfig, setAgentSipConfig] = useState(null);
  const [agentSipLoading, setAgentSipLoading] = useState(false);
  const [agentSipForm, setAgentSipForm] = useState({
    trunk_name: '',
    phone_numbers: '',
    allowed_addresses: '',
    allowed_ips: '',
    livekit_url: '',
    livekit_api_key: '',
    livekit_api_secret: '',
    room_prefix: '',
    is_enabled: false,
  });
  const [agentSipSubmitLoading, setAgentSipSubmitLoading] = useState(false);

  const fetchSip = useCallback(async () => {
    try {
      setSipLoading(true);
      const accessToken = user?.access_token || user?.token;
      const res = await axios.get('/api/sip/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const raw = res.data?.data?.sip || res.data?.data || res.data || [];
      setSipList(Array.isArray(raw) ? raw : []);
    } catch (err) {
      console.error('SIP fetch error:', err);
      setSipList([]);
    } finally {
      setSipLoading(false);
    }
  }, [user]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const accessToken = user?.access_token || user?.token;
      if (!accessToken) {
        setLoading(false);
        return;
      }

      const [numbersRes, callsRes] = await Promise.allSettled([
        axios.get('/api/get-twilio-numbers', {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        axios.get('/api/calls/', {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);

      if (numbersRes.status === 'fulfilled') {
        setNumbers(numbersRes.value.data || []);
      }
      if (callsRes.status === 'fulfilled') {
        setCalls(callsRes.value.data?.data?.calls || []);
      }
    } catch (err) {
      console.error('Telephony fetch error:', err);
      setError('Some telephony data could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchCallsOnly = useCallback(async () => {
    const accessToken = user?.access_token || user?.token;
    if (!accessToken) return;
    try {
      const callsRes = await axios.get('/api/calls/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (callsRes.data?.data?.calls) {
        setCalls(callsRes.data.data.calls);
      }
    } catch (err) {
      // Silent fail — polling is non-critical
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchData();
      fetchSip();
    }
  }, [user, fetchData, fetchSip]);

  // Real-time polling for calls when the calls tab is active
  useEffect(() => {
    if (activeTab !== 'calls') return;
    fetchCallsOnly();
    const interval = setInterval(fetchCallsOnly, 15000);
    return () => clearInterval(interval);
  }, [activeTab, fetchCallsOnly]);

  const parseCredentials = () => {
    try {
      return JSON.parse(sipForm.credentials || '{}');
    } catch {
      notifyError('Invalid JSON in credentials field. Please check the format.');
      return null;
    }
  };

  const handleCreateSip = async () => {
    if (!sipForm.provider.trim() || !sipForm.name.trim()) {
      notifyError('Provider and Name are required.');
      return;
    }
    const credentials = parseCredentials();
    if (credentials === null) return;
    setSipSubmitLoading(true);
    try {
      const accessToken = user?.access_token || user?.token;
      await axios.post(
        '/api/sip/create/',
        {
          provider: sipForm.provider.trim(),
          name: sipForm.name.trim(),
          credentials,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setSipModalVisible(false);
      setSipForm({ provider: '', name: '', credentials: '{}' });
      await fetchSip();
    } catch (err) {
      notifyError('Create SIP failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setSipSubmitLoading(false);
    }
  };

  const handleUpdateSip = async () => {
    if (!selectedSip) return;
    if (!sipForm.provider.trim() || !sipForm.name.trim()) {
      notifyError('Provider and Name are required.');
      return;
    }
    const credentials = parseCredentials();
    if (credentials === null) return;
    setSipSubmitLoading(true);
    try {
      const accessToken = user?.access_token || user?.token;
      await axios.patch(
        `/api/sip/${selectedSip.id}/update/`,
        {
          provider: sipForm.provider.trim(),
          name: sipForm.name.trim(),
          credentials,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setSipEditModalVisible(false);
      setSipForm({ provider: '', name: '', credentials: '{}' });
      await fetchSip();
    } catch (err) {
      notifyError('Update SIP failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setSipSubmitLoading(false);
    }
  };

  const handleDeleteSip = async () => {
    if (!selectedSip) return;
    try {
      const accessToken = user?.access_token || user?.token;
      await axios.delete(`/api/sip/${selectedSip.id}/delete/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setSipDeleteModalVisible(false);
      await fetchSip();
    } catch (err) {
      notifyError('Delete SIP failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const openSipEdit = (sip) => {
    setSelectedSip(sip);
    setSipForm({
      provider: sip.provider || '',
      name: sip.name || '',
      credentials: JSON.stringify(sip.credentials || {}),
    });
    setSipEditModalVisible(true);
  };

  // Agent SIP config functions
  const effectiveAgentId = (a) => a.id || a.adexcloud_agent_id;

  const fetchAgents = async () => {
    try {
      const accessToken = user?.access_token || user?.token;
      const res = await axios.get('/api/agents/list/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      // views.list_agents returns a raw array; agent_api.list_agents returns {data:{agents:[]}}
      const raw = Array.isArray(res.data)
        ? res.data
        : res.data?.data?.agents || res.data?.data || [];
      setAgents(Array.isArray(raw) ? raw : []);
    } catch (err) {
      console.error('Agent fetch error:', err);
      setAgents([]);
    }
  };

  const fetchAgentSipConfig = async (agentId) => {
    if (!agentId) return;
    try {
      setAgentSipLoading(true);
      const accessToken = user?.access_token || user?.token;
      const res = await axios.get(`/api/agents/${agentId}/sip/get/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const cfg = res.data?.data;
      setAgentSipConfig(cfg);
      if (cfg) {
        setAgentSipForm({
          trunk_name: cfg.trunk_name || '',
          phone_numbers: Array.isArray(cfg.phone_numbers) ? cfg.phone_numbers.join(', ') : '',
          allowed_addresses: Array.isArray(cfg.allowed_addresses) ? cfg.allowed_addresses.join(', ') : '',
          allowed_ips: Array.isArray(cfg.allowed_ips) ? cfg.allowed_ips.join(', ') : '',
          livekit_url: cfg.livekit_url || '',
          livekit_api_key: cfg.livekit_api_key || '',
          livekit_api_secret: cfg.livekit_api_secret || '',
          room_prefix: cfg.room_prefix || '',
          is_enabled: cfg.is_enabled || false,
        });
      } else {
        setAgentSipForm({
          trunk_name: '', phone_numbers: '', allowed_addresses: '', allowed_ips: '',
          livekit_url: '', livekit_api_key: '', livekit_api_secret: '', room_prefix: '', is_enabled: false,
        });
      }
    } catch (err) {
      console.error('Agent SIP fetch error:', err);
      setAgentSipConfig(null);
    } finally {
      setAgentSipLoading(false);
    }
  };

  const handleSaveAgentSipConfig = async () => {
    if (!selectedAgentId) return;
    setAgentSipSubmitLoading(true);
    try {
      const accessToken = user?.access_token || user?.token;
      const payload = {
        trunk_name: agentSipForm.trunk_name,
        phone_numbers: agentSipForm.phone_numbers.split(',').map((s) => s.trim()).filter(Boolean),
        allowed_addresses: agentSipForm.allowed_addresses.split(',').map((s) => s.trim()).filter(Boolean),
        allowed_ips: agentSipForm.allowed_ips.split(',').map((s) => s.trim()).filter(Boolean),
        livekit_url: agentSipForm.livekit_url,
        livekit_api_key: agentSipForm.livekit_api_key,
        livekit_api_secret: agentSipForm.livekit_api_secret,
        room_prefix: agentSipForm.room_prefix,
        is_enabled: agentSipForm.is_enabled,
      };
      await axios.post(`/api/agents/${selectedAgentId}/sip/`, payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      await fetchAgentSipConfig(selectedAgentId);
      notifySuccess('Agent SIP config saved successfully.');
    } catch (err) {
      notifyError('Save failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setAgentSipSubmitLoading(false);
    }
  };

  const handleCreateAgentTrunk = async () => {
    if (!selectedAgentId) return;
    try {
      const accessToken = user?.access_token || user?.token;
      const payload = {
        trunk_name: agentSipForm.trunk_name,
        phone_numbers: agentSipForm.phone_numbers.split(',').map((s) => s.trim()).filter(Boolean),
        allowed_addresses: agentSipForm.allowed_addresses.split(',').map((s) => s.trim()).filter(Boolean),
      };
      await axios.post(`/api/agents/${selectedAgentId}/sip/trunks/`, payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      await fetchAgentSipConfig(selectedAgentId);
      notifySuccess('Trunk created successfully.');
    } catch (err) {
      notifyError('Trunk creation failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCreateAgentDispatch = async () => {
    if (!selectedAgentId) return;
    try {
      const accessToken = user?.access_token || user?.token;
      const payload = {
        room_prefix: agentSipForm.room_prefix,
        metadata: { agentName: 'agent', agentId: selectedAgentId, type: 'inbound_sip' },
      };
      await axios.post(`/api/agents/${selectedAgentId}/sip/dispatch/`, payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      await fetchAgentSipConfig(selectedAgentId);
      notifySuccess('Dispatch rule created successfully.');
    } catch (err) {
      notifyError('Dispatch creation failed: ' + (err.response?.data?.message || err.message));
    }
  };

  return (
    <AppLayoutWrapper
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: 'Home', href: '/dashboard' },
            { text: 'Telephony', href: '/telephony' },
          ]}
          onFollow={(event) => {
            event.preventDefault();
            navigate(event.detail.href);
          }}
        />
      }
      header={{
        title: 'Telephony',
        description: 'Manage Twilio numbers and review call activity.',
        actions: (
          <SpaceBetween direction="horizontal" size="xs">
            <Button iconName="refresh" onClick={fetchData}>
              Refresh
            </Button>
            <Button variant="primary" iconName="add-plus" onClick={() => navigate('/telephony/new')}>
              Add Telephony
            </Button>
          </SpaceBetween>
        ),
      }}
      headerBackgroundStyle="linear-gradient(to bottom, rgba(9, 29, 43, 1) 2%, rgba(0, 108, 224, 1) 8%, rgba(255, 255, 255, 1) 82%)"
      helpPanelHeader="Telephony"
      helpPanelContent={
        <div>
          <p>Review phone numbers assigned to your agents and call transcripts.</p>
          <ul>
            <li>Twilio numbers provisioned per company</li>
            <li>Call transcripts and metadata</li>
            <li>Test calls via the agent detail panel</li>
          </ul>
        </div>
      }
    >
      {loading ? (
        <Box textAlign="center" padding={{ top: 'xxxl' }}>
          <Spinner size="large" />
        </Box>
      ) : error ? (
        <Alert type="warning" header="Partial data loaded">
          {error}
        </Alert>
      ) : (
        <SpaceBetween size="l">
          <ColumnLayout columns={{ default: 1, s: 2, m: 2, l: 4, xl: 4 }} variant="text-grid">
            <Container>
              <Box variant="awsui-key-label"><Icon name="phone" variant="normal" /> Twilio Numbers</Box>
              <Box variant="h1">{numbers.length}</Box>
            </Container>
            <Container>
              <Box variant="awsui-key-label"><Icon name="settings" variant="normal" /> SIP Configurations</Box>
              <Box variant="h1">{sipList.length}</Box>
            </Container>
            <Container>
              <Box variant="awsui-key-label"><Icon name="call" variant="normal" /> Total Calls</Box>
              <Box variant="h1">{calls.length}</Box>
            </Container>
            <Container>
              <Box variant="awsui-key-label"><Icon name="status-positive" variant="normal" /> Active Numbers</Box>
              <Box variant="h1" color="text-status-success">
                {numbers.filter((n) => n.status === 'active').length}
              </Box>
            </Container>
          </ColumnLayout>

          <Tabs
            activeTabId={activeTab}
            onChange={({ detail }) => setActiveTab(detail.activeTabId)}
            tabs={[
              {
                id: 'numbers',
                label: 'Phone Numbers',
                content: (
                  <Container>
                    <Table
                      columnDefinitions={[
                        {
                          id: 'phone',
                          header: 'Phone Number',
                          cell: (item) => item.phone_number || 'N/A',
                          sortingField: 'phone',
                        },
                        {
                          id: 'status',
                          header: 'Status',
                          sortingField: 'status',
                          cell: (item) => (
                            <StatusIndicator
                              type={
                                item.status === 'active'
                                  ? 'success'
                                  : item.status === 'pending'
                                  ? 'pending'
                                  : 'info'
                              }
                            >
                              {item.status || 'Unknown'}
                            </StatusIndicator>
                          ),
                        },
                        {
                          id: 'assigned_to',
                          header: 'Assigned to',
                          cell: (item) => {
                            if (item.assigned_agent_name || item.assigned_agent?.name) {
                              return <StatusIndicator type="success">{item.assigned_agent_name || item.assigned_agent?.name}</StatusIndicator>;
                            }
                            if (item.assigned_pack_name || item.assigned_pack?.name) {
                              return <StatusIndicator type="info">{item.assigned_pack_name || item.assigned_pack?.name} (pack)</StatusIndicator>;
                            }
                            return <StatusIndicator type="pending">Unassigned</StatusIndicator>;
                          },
                        },
                        {
                          id: 'id',
                          header: 'ID',
                          cell: (item) => (
                            <Box
                              variant="small"
                              color="text-body-secondary"
                            >
                              {item.id}
                            </Box>
                          ),
                        },
                      ]}
                      items={sortedNumbers}
                      loadingText="Loading phone numbers"
                      trackBy="id"
                      stickyHeader
                      sortable
                      sortingColumn={numbersSortingColumn}
                      sortingDescending={numbersSortingDescending}
                      onSortingChange={({ detail }) => {
                        setNumbersSortingColumn(detail.sortingColumn);
                        setNumbersSortingDescending(detail.isDescending);
                      }}
                      empty={
                        <Box textAlign="center" padding="l">
                          <SpaceBetween size="m">
                            <b>No numbers found</b>
                            <p>
                              Numbers are provisioned during the agent
                              wizard.
                            </p>
                            <Button onClick={() => navigate('/Wizard')}>Create an Agent</Button>
                          </SpaceBetween>
                        </Box>
                      }
                    />
                  </Container>
                ),
              },
              {
                id: 'sip',
                label: 'SIP Configurations',
                content: (
                  <Container
                    header={
                      <Header
                        variant="h2"
                        counter={`(${sipList.length})`}
                        actions={
                          <Button iconName="add-plus" onClick={() => setSipModalVisible(true)}>
                            Add SIP
                          </Button>
                        }
                      >
                        SIP Configurations
                      </Header>
                    }
                  >
                    {sipLoading ? (
                      <Box textAlign="center" padding="l"><Spinner size="large" /></Box>
                    ) : (
                      <Table
                        columnDefinitions={[
                          {
                            id: 'name',
                            header: 'Name',
                            cell: (item) => item.agent_name || item.name || item.sip?.name || 'Unnamed',
                            sortingField: 'name',
                          },
                          {
                            id: 'provider',
                            header: 'Provider',
                            cell: (item) => item.sip?.provider || item.provider || 'N/A',
                            sortingField: 'provider',
                          },
                          {
                            id: 'assigned_to',
                            header: 'Assigned to',
                            cell: (item) => {
                              const agentName = item.agent_name || item.assigned_agent_name || item.assigned_agent?.name;
                              if (agentName) {
                                return <StatusIndicator type="success">{agentName}</StatusIndicator>;
                              }
                              return <StatusIndicator type="pending">Unassigned</StatusIndicator>;
                            },
                          },
                          {
                            id: 'status',
                            header: 'Status',
                            cell: (item) => {
                              const active = item.sip?.active !== undefined ? item.sip.active : item.active;
                              const status = item.sip?.status || item.status;
                              return (
                                <StatusIndicator type={active !== false ? 'success' : 'stopped'}>
                                  {status || (active !== false ? 'Active' : 'Inactive')}
                                </StatusIndicator>
                              );
                            },
                          },
                          {
                            id: 'actions',
                            header: 'Actions',
                            cell: (item) => (
                              <SpaceBetween direction="horizontal" size="xs">
                                <Button iconName="edit" variant="icon" onClick={() => openSipEdit(item)} title="Edit" />
                                <Button iconName="remove" variant="icon" onClick={() => { setSelectedSip(item); setSipDeleteModalVisible(true); }} title="Delete" />
                              </SpaceBetween>
                            ),
                          },
                        ]}
                        items={sipList}
                        loadingText="Loading SIP configurations"
                        trackBy={(item) => item.id || item.agent_id || item.sip?.id}
                        stickyHeader
                        empty={
                          <Box textAlign="center" padding="l">
                            <SpaceBetween size="m">
                              <b>No SIP configurations found</b>
                              <p>Add a SIP trunk configuration for your telephony.</p>
                              <Button onClick={() => setSipModalVisible(true)}>Add SIP</Button>
                            </SpaceBetween>
                          </Box>
                        }
                      />
                    )}
                  </Container>
                ),
              },
              {
                id: 'agent-sip',
                label: 'Agent SIP',
                content: (
                  <Container header={<Header variant="h2">Agent SIP Configuration</Header>}>
                    <SpaceBetween size="l">
                      <FormField label="Select Agent">
                        <Select
                          selectedOption={agents.find((a) => effectiveAgentId(a) === selectedAgentId)
                            ? { label: agents.find((a) => effectiveAgentId(a) === selectedAgentId).name, value: selectedAgentId }
                            : null}
                          onChange={({ detail }) => {
                            const val = detail.selectedOption?.value;
                            setSelectedAgentId(val);
                            if (val) fetchAgentSipConfig(val);
                          }}
                          options={agents.map((a) => ({ label: a.name || 'Unnamed', value: effectiveAgentId(a) })).filter((o) => o.value)}
                          placeholder="Choose an agent"
                          onFocus={fetchAgents}
                        />
                      </FormField>
                      {agentSipLoading ? (
                        <Box textAlign="center" padding="l"><Spinner size="large" /></Box>
                      ) : selectedAgentId ? (
                        <SpaceBetween size="l">
                          <FormField label="Trunk Name">
                            <Input
                              value={agentSipForm.trunk_name}
                              onChange={({ detail }) => setAgentSipForm((prev) => ({ ...prev, trunk_name: detail.value }))}
                              placeholder="inbound-medical-trunk"
                            />
                          </FormField>
                          <FormField label="Phone Numbers (comma-separated)">
                            <Input
                              value={agentSipForm.phone_numbers}
                              onChange={({ detail }) => setAgentSipForm((prev) => ({ ...prev, phone_numbers: detail.value }))}
                              placeholder="+33123456789, +33698765432"
                            />
                          </FormField>
                          <FormField label="Allowed Addresses (comma-separated)">
                            <Input
                              value={agentSipForm.allowed_addresses}
                              onChange={({ detail }) => setAgentSipForm((prev) => ({ ...prev, allowed_addresses: detail.value }))}
                              placeholder="sip:provider1.com, sip:provider2.com"
                            />
                          </FormField>
                          <FormField label="Allowed IPs (comma-separated)">
                            <Input
                              value={agentSipForm.allowed_ips}
                              onChange={({ detail }) => setAgentSipForm((prev) => ({ ...prev, allowed_ips: detail.value }))}
                              placeholder="203.0.113.0/24, 198.51.100.10"
                            />
                          </FormField>
                          <FormField label="LiveKit URL">
                            <Input
                              value={agentSipForm.livekit_url}
                              onChange={({ detail }) => setAgentSipForm((prev) => ({ ...prev, livekit_url: detail.value }))}
                              placeholder="wss://livekit.yourdomain.com"
                            />
                          </FormField>
                          <FormField label="LiveKit API Key">
                            <Input
                              value={agentSipForm.livekit_api_key}
                              onChange={({ detail }) => setAgentSipForm((prev) => ({ ...prev, livekit_api_key: detail.value }))}
                              placeholder="APIyourKeyHere"
                            />
                          </FormField>
                          <FormField label="LiveKit API Secret">
                            <Input
                              value={agentSipForm.livekit_api_secret}
                              onChange={({ detail }) => setAgentSipForm((prev) => ({ ...prev, livekit_api_secret: detail.value }))}
                              placeholder="yourSecretHere"
                            />
                          </FormField>
                          <FormField label="Room Prefix">
                            <Input
                              value={agentSipForm.room_prefix}
                              onChange={({ detail }) => setAgentSipForm((prev) => ({ ...prev, room_prefix: detail.value }))}
                              placeholder="medical-call-"
                            />
                          </FormField>
                          <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="primary" onClick={handleSaveAgentSipConfig} loading={agentSipSubmitLoading}>
                              Save Config
                            </Button>
                            <Button onClick={handleCreateAgentTrunk}>Create Trunk</Button>
                            <Button onClick={handleCreateAgentDispatch}>Create Dispatch Rule</Button>
                          </SpaceBetween>
                          {agentSipConfig?.ourSipUri && (
                            <Alert type="info">
                              Our SIP URI: <b>{agentSipConfig.ourSipUri}</b> (give this to your SIP provider)
                            </Alert>
                          )}
                          {agentSipConfig?.livekit_trunk_id && (
                            <Alert type="success">
                              Trunk ID: {agentSipConfig.livekit_trunk_id} | Status: {agentSipConfig.trunk_status || 'N/A'}
                            </Alert>
                          )}
                          {agentSipConfig?.livekit_dispatch_rule_id && (
                            <Alert type="success">
                              Dispatch ID: {agentSipConfig.livekit_dispatch_rule_id} | Status: {agentSipConfig.dispatch_status || 'N/A'}
                            </Alert>
                          )}
                        </SpaceBetween>
                      ) : (
                        <Box textAlign="center" padding="l">Select an agent to configure SIP.</Box>
                      )}
                    </SpaceBetween>
                  </Container>
                ),
              },
              {
                id: 'calls',
                label: 'Calls',
                content: (
                  <Container>
                    <TextFilter
                      filteringText={callsFilteringText}
                      filteringPlaceholder="Find calls by company or call ID"
                      onChange={({ detail }) => setCallsFilteringText(detail.filteringText)}
                    />
                    <Table
                      columnDefinitions={[
                        {
                          id: 'id',
                          header: 'Call ID',
                          sortingField: 'id',
                          cell: (item) => (
                            <Box
                              variant="small"
                              color="text-body-secondary"
                            >
                              {item.id}
                            </Box>
                          ),
                        },
                        {
                          id: 'company',
                          header: 'Company',
                          sortingField: 'company',
                          cell: (item) =>
                            item.assistant?.company || 'N/A',
                        },
                        {
                          id: 'transcript',
                          header: 'Transcript',
                          cell: (item) => (
                            <Box
                              variant="small"
                              color="text-body-secondary"
                              style={{
                                maxWidth: '300px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {item.transcript || 'No transcript'}
                            </Box>
                          ),
                        },
                        {
                          id: 'created',
                          header: 'Created',
                          sortingField: 'created',
                          cell: (item) =>
                            item.created_at
                              ? new Date(
                                  item.created_at
                                ).toLocaleString()
                              : 'N/A',
                        },
                      ]}
                      items={sortedCalls}
                      loadingText="Loading calls"
                      trackBy="id"
                      stickyHeader
                      sortable
                      sortingColumn={callsSortingColumn}
                      sortingDescending={callsSortingDescending}
                      onSortingChange={({ detail }) => {
                        setCallsSortingColumn(detail.sortingColumn);
                        setCallsSortingDescending(detail.isDescending);
                      }}
                      onRowClick={({ detail }) => {
                        setSelectedCall(detail.item);
                        setModalVisible(true);
                      }}
                      empty={
                        <Box textAlign="center" padding="l">
                          <SpaceBetween size="m">
                            <b>No calls yet</b>
                            <p>
                              Calls will appear once your agents start
                              receiving traffic.
                            </p>
                          </SpaceBetween>
                        </Box>
                      }
                    />
                  </Container>
                ),
              },
            ]}
          />

          {selectedCall && (
            <Modal
              visible={modalVisible}
              onDismiss={() => setModalVisible(false)}
              header={<Header variant="h2">Call Details</Header>}
              size="large"
              footer={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="primary" onClick={() => setModalVisible(false)}>
                    Close
                  </Button>
                </SpaceBetween>
              }
            >
              <SpaceBetween size="m">
                <ColumnLayout columns={2} variant="text-grid">
                  <div>
                    <Box variant="awsui-key-label">Call ID</Box>
                    <div>{selectedCall.id}</div>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Company</Box>
                    <div>{selectedCall.assistant?.company || 'N/A'}</div>
                  </div>
                  <div>
                    <Box variant="awsui-key-label">Created</Box>
                    <div>
                      {selectedCall.created_at
                        ? new Date(selectedCall.created_at).toLocaleString()
                        : 'N/A'}
                    </div>
                  </div>
                </ColumnLayout>
                <Container header={<Header variant="h3">Transcript</Header>}>
                  <TextContent>
                    <p style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedCall.transcript || 'No transcript available.'}
                    </p>
                  </TextContent>
                </Container>
              </SpaceBetween>
            </Modal>
          )}

          {/* SIP Create Modal */}
          {sipModalVisible && (
            <Modal
              visible={sipModalVisible}
              onDismiss={() => setSipModalVisible(false)}
              header="Add SIP Configuration"
              footer={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => setSipModalVisible(false)}>Cancel</Button>
                  <Button variant="primary" onClick={handleCreateSip} loading={sipSubmitLoading}>Create</Button>
                </SpaceBetween>
              }
            >
              <SpaceBetween size="l">
                <FormField label="Provider" constraintText="Required">
                  <Input
                    value={sipForm.provider}
                    onChange={({ detail }) => setSipForm((prev) => ({ ...prev, provider: detail.value }))}
                    placeholder="twilio, vonage, etc."
                  />
                </FormField>
                <FormField label="Name" constraintText="Required">
                  <Input
                    value={sipForm.name}
                    onChange={({ detail }) => setSipForm((prev) => ({ ...prev, name: detail.value }))}
                    placeholder="My SIP Trunk"
                  />
                </FormField>
                <FormField label="Credentials (JSON)">
                  <Textarea
                    value={sipForm.credentials}
                    onChange={({ detail }) => setSipForm((prev) => ({ ...prev, credentials: detail.value }))}
                    placeholder='{"api_key": "...", "api_secret": "..."}'
                  />
                </FormField>
              </SpaceBetween>
            </Modal>
          )}

          {/* SIP Edit Modal */}
          {sipEditModalVisible && (
            <Modal
              visible={sipEditModalVisible}
              onDismiss={() => setSipEditModalVisible(false)}
              header="Edit SIP Configuration"
              footer={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => setSipEditModalVisible(false)}>Cancel</Button>
                  <Button variant="primary" onClick={handleUpdateSip} loading={sipSubmitLoading}>Save</Button>
                </SpaceBetween>
              }
            >
              <SpaceBetween size="l">
                <FormField label="Provider" constraintText="Required">
                  <Input
                    value={sipForm.provider}
                    onChange={({ detail }) => setSipForm((prev) => ({ ...prev, provider: detail.value }))}
                  />
                </FormField>
                <FormField label="Name" constraintText="Required">
                  <Input
                    value={sipForm.name}
                    onChange={({ detail }) => setSipForm((prev) => ({ ...prev, name: detail.value }))}
                  />
                </FormField>
                <FormField label="Credentials (JSON)">
                  <Textarea
                    value={sipForm.credentials}
                    onChange={({ detail }) => setSipForm((prev) => ({ ...prev, credentials: detail.value }))}
                  />
                </FormField>
              </SpaceBetween>
            </Modal>
          )}

          {/* SIP Delete Modal */}
          {sipDeleteModalVisible && (
            <Modal
              visible={sipDeleteModalVisible}
              onDismiss={() => setSipDeleteModalVisible(false)}
              header="Delete SIP Configuration"
              footer={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => setSipDeleteModalVisible(false)}>Cancel</Button>
                  <Button variant="primary" onClick={handleDeleteSip}>Delete</Button>
                </SpaceBetween>
              }
            >
              Are you sure you want to delete <b>{selectedSip?.name}</b>?
            </Modal>
          )}
        </SpaceBetween>
      )}
    </AppLayoutWrapper>
  );
}
